// NOTE: The export from ui-core is CalculatorDescriptorProvider (singular) not CalculatorDescriptorsProvider (plural)
import CalculatorDescriptorProvider from '@e2grnd/ui-core/dist/contexts/CalculatorDescriptorsProvider'
import type { CalculatorDescriptorMap, CalculatorGroupDescriptorMap } from '@e2grnd/ui-core/dist/types'
import { parse, stringify } from 'query-string'
import type { PropsWithChildren } from 'react'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'

import calculatorGroups from '../meta/calculator-groups.json'
import calculatorManifest from '../meta/calculator-manifest.json'
import icons from '../meta/icons'

export default function CalcDescriptorsProviderVite({ children }: PropsWithChildren) {
  // EXTREME DEFENSIVE CODING - Initialize everything as empty objects
  const rawManifest = calculatorManifest || []
  const rawGroups = calculatorGroups || {}
  const rawIcons = icons || {}

  // Initialize with empty objects, never undefined
  const calculatorDescriptors: CalculatorDescriptorMap = {}
  const groups: CalculatorGroupDescriptorMap = {}

  // Transform the calculator manifest into the format expected by ui-core
  try {
    // calculatorManifest is an array of calculator data with merged titles
    // biome-ignore lint/suspicious/noExplicitAny: manifest entries have dynamic shape from JSON
    rawManifest.forEach((calc: any) => {
      if (calc?.calcId) {
        const Icon = rawIcons[calc.calcId as keyof typeof rawIcons]

        // Extract the descriptor data with title included
        calculatorDescriptors[calc.calcId] = {
          ...calc.descriptor, // Technical descriptor data
          title: calc.title || calc.name || calc.calcId, // Add title from merged data
          name: calc.name || calc.title || calc.calcId,
          sortTitle: calc.sortTitle || undefined,
          shortName: calc.shortName || calc.shortTitle || calc.title,
          shortTitle: calc.shortTitle || calc.shortName || calc.title,
          shortDescription: calc.shortDescription || '',
          groups: calc.groups || [], // Group IDs for filtering
          equipmentTypes: calc.equipmentTypes || [],
          analysisTypes: calc.analysisTypes || [],
          icon: Icon || undefined,
          userManualPath: calc.userManualPath || undefined,
          status: calc.status || 'published',
          helpContent: calc.helpContent || undefined,
          marketingBlurbLong: calc.marketingBlurbLong || undefined,
          changeLog: calc.changeLog || undefined,
        } as unknown as CalculatorDescriptorMap[string]
      }
    })
  } catch (error) {
    console.error('Error processing calculator manifest:', error)
  }

  // Use groups directly - handle both array format and object format
  try {
    if (rawGroups && typeof rawGroups === 'object') {
      // biome-ignore lint/suspicious/noExplicitAny: group data has dynamic shape from JSON
      Object.entries(rawGroups).forEach(([groupId, groupData]: [string, any]) => {
        if (groupId && groupData) {
          // Handle array format (groupData is directly the members array)
          if (Array.isArray(groupData)) {
            groups[groupId] = {
              groupId,
              title: groupId,
              shortTitle: groupId,
              shortDescription: `${groupId} calculators`,
              members: groupData,
            }
          }
          // Handle object format (groupData has a members property)
          else if (groupData.members) {
            groups[groupId] = {
              groupId: groupData.groupId || groupId,
              title: groupData.title || groupId,
              shortTitle: groupData.shortTitle || groupData.title || groupId,
              shortDescription: groupData.shortDescription || `${groupData.title || groupId} calculators`,
              members: groupData.members || [],
            }
          }
        }
      })
    }
  } catch (error) {
    console.error('Error processing groups:', error)
  }

  // GUARANTEED FALLBACK - Always have at least one group
  if (Object.keys(groups).length === 0) {
    groups.default = {
      groupId: 'default',
      title: 'All Calculators',
      shortTitle: 'All',
      shortDescription: 'All available calculators',
      members: Object.keys(calculatorDescriptors),
    }
  }

  // Final safety check - ensure we have valid objects
  const finalCalculators = Object.keys(calculatorDescriptors).length > 0 ? calculatorDescriptors : {}

  const finalGroups =
    Object.keys(groups).length > 0
      ? groups
      : {
          default: {
            groupId: 'default',
            title: 'All',
            shortTitle: 'All',
            shortDescription: 'All calculators',
            members: [],
          },
        }

  return (
    <QueryParamProvider
      adapter={ReactRouter6Adapter}
      options={{
        searchStringToObject: parse,
        objectToSearchString: stringify,
      }}
    >
      <CalculatorDescriptorProvider
        calculatorDescriptors={finalCalculators}
        groupDescriptors={finalGroups}
        iconMap={rawIcons}
      >
        {children}
      </CalculatorDescriptorProvider>
    </QueryParamProvider>
  )
}
